import { Component, OnInit } from '@angular/core';
import { ReservationQueriesService } from '../../../shared/services/reservation-queries.service';
import { ReservationActionsService } from '../../../shared/services/reservation-actions.service';
import { from, map, Observable, tap } from 'rxjs';
import { LetDirective } from '@ngrx/component';
import { AsyncPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { Router } from '@angular/router';
import { CancelReservationComponent } from '../../cancel-reservation/cancel-reservation.component';
import { MatDialog } from '@angular/material/dialog';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { UserService } from '../../../shared/services/user.service';
import { RecurringBookingsService } from '../../settings/services/recurring-bookings.service';
import { Reservation } from 'src/app/shared/models/reservations.model';
import { ClubService } from 'src/app/shared/services/club.service';
import { ActivelyLinkService } from 'src/app/shared/services/actively-link.service';
import { UserReservationHistoryComponent } from 'src/app/modules/my-reservations/user-reservation-history/user-reservation-history.component';

@Component({
  selector: 'app-my-reservations',
  imports: [
    LetDirective,
    MatIcon,
    TranslatePipe,
    UserReservationHistoryComponent,
  ],
  templateUrl: './my-reservations.component.html',
  styleUrl: './my-reservations.component.scss',
})
export class MyReservationsComponent implements OnInit {
  reservationsMadeFromUser$: Observable<Reservation[] | null> | undefined;
  reservationsUserIsIncluded$: Observable<Reservation[]> | undefined;
  recurringReservations$: Observable<unknown> | undefined;
  pendingReservations$: Observable<any[]> | undefined;
  isExpanded: { [reservationId: number]: boolean } = {};
  userNames: { [reservationId: number]: string[] } = {};
  userRole$: Observable<string | null> | undefined;
  joinTheQueueFeature: boolean | undefined;
  courtName: boolean | undefined;
  only_recurring: boolean | undefined;
  users_involved: boolean | undefined;
  cancelation_policy: string | undefined;
  fixed_time: string | undefined;
  hours_before: number | undefined;
  single_court: boolean | undefined;
  combined: boolean | undefined;
  show_recurring_participants: boolean | undefined;

  weekdays = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  constructor(
    private queriesService: ReservationQueriesService,
    private actionsService: ReservationActionsService,
    private router: Router,
    private dialog: MatDialog,
    protected translationService: TranslationService,
    private userService: UserService,
    private recurringReservationsService: RecurringBookingsService,
    private clubsService: ClubService,
    private activelyLinkService: ActivelyLinkService,
    public translateService: TranslationService
  ) {}

  async toggleExpanded(reservationId: number, reservation: Reservation) {
    this.isExpanded[reservationId] = !this.isExpanded[reservationId];
    if (this.isExpanded[reservationId] && !this.userNames[reservationId]) {
      const { data: userNames } =
        await this.queriesService.getNamesFromUserIds(
          reservation.usersInvolved ?? []
        );
      if (userNames) {
        this.userNames[reservationId] = userNames.map(
          (user) => user.fullname || ''
        );
        this.userNames[reservationId].push(reservation.booked_by_user_name);
      }
    }
  }

  editReservation(reservation: Reservation) {
    const reservationNumber = encodeURI(String(reservation.id));
    const reservationUsersInvolved = encodeURI(reservation.usersInvolved?.join(',') ?? '');
    this.router.navigate([
      'reservation',
      reservationNumber,
      reservationUsersInvolved,
    ]);
  }

  removeFromQueue(reservationId: string) {
    this.actionsService.removeUserFromReservationQueue(reservationId);
  }

  cancelReservation(reservation: Reservation) {
    const dialogRef = this.dialog
      .open(CancelReservationComponent, {
        width: '400px',
        data: {},
      })
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.actionsService
            .cancelReservationByUser(
              new Date(reservation.startDateAndTime),
              new Date(reservation.endDateAndTime),
              reservation.courtName
            )
            .then((cancelResult) => {
              if (cancelResult === 'Cancelation successful!') {
                this.refreshReservations();
              }
            });
        }
      });
  }
  ngOnInit() {
    this.refreshReservations();
    this.getRecurringReservations();
    this.pendingReservations$ = from(
      this.queriesService.getPendingReservationsForCurrentUser()
    ).pipe(map((res) => res.data ?? []));
    this.userRole$ = this.userService.getUserRole();

    const config = this.clubsService.flags();
    const theme_config = this.clubsService.theme();
    const cancelationSettings = this.clubsService.cancelation_settings();
    const reservationSettings = this.clubsService.reservationSettings();

    this.combined = reservationSettings?.combined_bookings_for_time_slots;
    this.show_recurring_participants =
      reservationSettings?.show_recurring_reservation_participants ?? true;
    this.joinTheQueueFeature = config?.join_queue_feature;
    this.courtName = config?.court_names;
    this.only_recurring = config?.only_recurring_bookings;
    this.users_involved = config?.add_reservation_participants;
    this.single_court = config?.single_court;
    this.cancelation_policy = cancelationSettings?.policy;
    this.fixed_time = cancelationSettings?.fixed_time;
    this.hours_before = cancelationSettings?.hours_before;
  }

  private getRecurringReservations() {
    this.recurringReservations$ = from(
      this.recurringReservationsService.getRecurringBookings()
    ).pipe(
      map((res) => res.data),
      tap((res) => console.log(res))
    );
  }

  private refreshReservations() {
    this.reservationsMadeFromUser$ = from(
      this.queriesService.getReservationsMadeFromCurrentUser()
    ).pipe(
      map((res) => res.data),
      tap((res) => {
        if (res) {
          res.forEach(
            (reservation) => (this.isExpanded[reservation.id] = false)
          );
        }
      })
    );

    this.reservationsUserIsIncluded$ = from(
      this.queriesService.getReservationsCurrentUserIsInvolved()
    ).pipe(
      map((res) => res.data ?? []),
      tap((reservations: Reservation[]) => {
        reservations.forEach((reservation) => {
          this.isExpanded[reservation.id] = false;
        });
      })
    );
  }

  isAllowedToEdit(reservation: Reservation) {
    const now = Date.now();
    const rDate = now - new Date(reservation.startDateAndTime).getTime();
    return rDate < 0;
  }

  isAllowedToCancel(reservation: Reservation) {
    const now = Date.now();

    if (this.cancelation_policy === 'fixed_time' && this.fixed_time) {
      const now = new Date();

      const reservationDate = new Date(reservation.startDateAndTime);

      const isSameDate =
        now.getFullYear() === reservationDate.getFullYear() &&
        now.getMonth() === reservationDate.getMonth() &&
        now.getDate() === reservationDate.getDate();

      if (isSameDate) {
        const [hours, minutes, seconds] = this.fixed_time
          .split(':')
          .map(Number);

        const fixedDate = new Date();
        fixedDate.setHours(hours, minutes, seconds || 0, 0);

        return now.getTime() < fixedDate.getTime();
      }

      return true;
    } else if (
      this.cancelation_policy === 'hours_before' &&
      this.hours_before
    ) {
      const startTime = new Date(reservation.startDateAndTime).getTime();

      const cutoffTime = startTime - this.hours_before * 60 * 60 * 1000;
      return now < cutoffTime;
    }

    return true;
  }

  protected readonly Date = Date;

  getTime(startDateTime: string | Date, endDateTime: string | Date): string {
    const startTime = new Date(startDateTime);
    const endTime = new Date(endDateTime);

    return `${startTime.toTimeString().slice(0, 5)} - ${endTime.toTimeString().slice(0, 5)}`;
  }

  cancelMatchmakingReservation(reservation: Reservation) {
    this.dialog
      .open(CancelReservationComponent, {
        width: '400px',
        data: {},
      })
      .afterClosed()
      .subscribe(async (confirmed) => {
        if (confirmed) {
          try {
            await this.activelyLinkService.cancel(reservation.id);
            this.refreshReservations();
          } catch (err) {
            console.error('Failed to cancel matchmaking reservation:', err);
          }
        }
      });
  }

  leaveRucurring(id: number | string) {
    this.actionsService.leaveRecurringBooking(String(id));
  }

  getDateName(date: string): string {
    const day = new this.Date(date).getDay();
    return this.translateService.translateDays(this.weekdays[day], 'DAYS');
  }
}
